import { Link } from 'react-router-dom';
import { Archive, ArchiveRestore, ArrowRightCircle, ExternalLink } from 'lucide-react';
import type { Lead } from '../../../lib/leadsApi';
import { RelativeTime } from '../../ui/RelativeTime';
import {
  LeadAssigneeEditor,
  LeadDateEditor,
  LeadSourceEditor,
  LeadStageEditor,
  LeadTemperatureEditor,
  LeadTextEditor,
  LeadValueEditor,
} from './leadInlineEditors';
import { LeadLabelEditor } from './LeadLabelEditor';
import { LeadClientEditor } from './LeadClientEditor';

/**
 * CLE-192 — right-hand properties sidebar (~280px) for the lead detail page.
 *
 * One row per property: label on the left, the editable value on the right.
 * Stage / temperature / assignee / source / dates reuse the lead inline popover
 * editors; value+currency and contact fields are inline. A prominent "Convert
 * to Project" action shows on non-terminal stages; Archive is always available.
 */

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="-mx-2 flex min-h-[34px] items-start gap-2 rounded px-2 py-1 transition-colors hover:bg-white/[0.03]">
      <span className="mt-1 w-[92px] shrink-0 text-[12px] text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 items-center justify-start">{children}</div>
    </div>
  );
}

export function LeadDetailSidebar({
  lead,
  onConvert,
  onArchive,
  isArchiving,
  onRestore,
  isRestoring,
  inSheet = false,
}: {
  lead: Lead;
  onConvert: () => void;
  onArchive: () => void;
  isArchiving?: boolean;
  onRestore: () => void;
  isRestoring?: boolean;
  /** Inside the phone sheet; otherwise it is the desktop column, from lg up. */
  inSheet?: boolean;
}) {
  const isConverted = Boolean(lead.convertedToProjectId);
  const stageType = lead.stage?.type ?? 'ACTIVE';
  // Convert is offered only on non-terminal stages (not WON/LOST/ARCHIVED) and
  // when not already converted.
  const canConvert = !isConverted && stageType === 'ACTIVE';

  return (
    <aside
      className={
        inSheet
          ? 'w-full px-4 py-4'
          : 'hidden w-[290px] shrink-0 border-l border-gray-200 px-4 py-4 lg:block'
      }
    >
      <h2 className="mb-2 px-0 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Proprietăți
      </h2>

      <Row label="Etapă">
        <LeadStageEditor lead={lead} />
      </Row>

      <Row label="Temperatură">
        <LeadTemperatureEditor lead={lead} />
      </Row>

      <Row label="Responsabil">
        <LeadAssigneeEditor lead={lead} />
      </Row>

      <Row label="Valoare">
        <LeadValueEditor lead={lead} />
      </Row>

      <Row label="Sursă">
        <LeadSourceEditor lead={lead} />
      </Row>

      <Row label="Data închiderii">
        <LeadDateEditor
          lead={lead}
          field="expectedCloseDate"
          placeholder="Setează data închiderii"
        />
      </Row>

      <div className="my-3 border-t border-gray-200" />

      <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        Contact
      </h3>

      <Row label="Nume">
        <LeadTextEditor lead={lead} field="contactName" placeholder="Nume contact" />
      </Row>
      <Row label="Email">
        <LeadTextEditor lead={lead} field="contactEmail" placeholder="Email" type="email" />
      </Row>
      <Row label="Telefon">
        <LeadTextEditor lead={lead} field="contactPhone" placeholder="Telefon" type="tel" />
      </Row>
      <Row label="Companie">
        <LeadTextEditor lead={lead} field="companyName" placeholder="Companie" />
      </Row>
      <Row label="Client">
        <LeadClientEditor lead={lead} />
      </Row>

      <div className="my-3 border-t border-gray-200" />

      <Row label="Etichete">
        <LeadLabelEditor lead={lead} />
      </Row>

      <Row label="Ultimul contact">
        <LeadDateEditor lead={lead} field="lastContactedAt" placeholder="Niciodată" />
      </Row>

      <Row label="Creat">
        <RelativeTime iso={lead.createdAt} className="px-1 text-[13px] text-gray-500" />
      </Row>

      <div className="mt-4 space-y-2 border-t border-gray-200 pt-4">
        {isConverted && lead.convertedToProjectId ? (
          <Link
            to={`/projects/${lead.convertedToProjectId}`}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-[13px] font-medium text-green-600 hover:bg-green-500/20"
          >
            <ExternalLink size={14} /> Vezi proiectul
          </Link>
        ) : (
          canConvert && (
            <button
              type="button"
              onClick={onConvert}
              className="flex w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-brand-700"
            >
              <ArrowRightCircle size={15} /> Convertește în proiect
            </button>
          )
        )}

        {lead.archivedAt ? (
          <>
            <p className="text-center text-[12px] text-gray-500">
              Arhivat <RelativeTime iso={lead.archivedAt} />
            </p>
            <button
              type="button"
              onClick={onRestore}
              disabled={isRestoring}
              className="flex w-full items-center justify-center gap-1.5 rounded-md border border-brand-500/40 bg-brand-500/10 px-3 py-2 text-[13px] font-medium text-brand-600 hover:bg-brand-500/20 disabled:opacity-50"
            >
              <ArchiveRestore size={14} /> {isRestoring ? 'Se restaurează…' : 'Restaurează'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={onArchive}
            disabled={isArchiving}
            className="flex w-full items-center justify-center gap-1.5 rounded-md border border-gray-200 px-3 py-2 text-[13px] font-medium text-gray-500 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-50"
          >
            <Archive size={14} /> {isArchiving ? 'Se arhivează…' : 'Arhivează'}
          </button>
        )}
      </div>
    </aside>
  );
}
